import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, tap } from 'rxjs';

import { LoggerName, LogLevel } from '@ninety/mobile/platform/api';
import { DatadogLogger, DatadogLogsService } from '@ninety/mobile/platform/services';

import { LoggingActions } from './app-logging.actions';

@Injectable()
export class LoggingEffects {
  private actions$ = inject(Actions);
  private datadogLogger = inject(DatadogLogger);
  private datadogLogsService = inject(DatadogLogsService);

  /** Fetch a logger by name or fallback to the AppModule logger */
  private getLogger(name: LoggerName): DatadogLogger {
    const logger = this.datadogLogsService.getLogger(name);

    return logger ? logger : this.datadogLogger;
  }

  createLogger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoggingActions.createLogger),
      map(({ name, conf }) => {
        const logger = this.datadogLogsService.createLogger(String(name), conf);

        logger.setMinLevel((conf?.level as LogLevel) ?? LogLevel.Error);

        return LoggingActions.createLoggerSuccess({ name, logger });
      })
    )
  );

  log$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoggingActions.debug, LoggingActions.error, LoggingActions.info, LoggingActions.warn),
        tap(({ log, loggerName, type }) => {
          const logger = this.getLogger(loggerName ?? LoggerName.AppModule);
          switch (type) {
            case LoggingActions.debug.type:
              logger.debug(log);
              break;
            case LoggingActions.error.type:
              logger.error(log);
              break;
            case LoggingActions.info.type:
              logger.info(log);
              break;
            case LoggingActions.warn.type:
              logger.warn(log);
              break;
          }
        })
      ),
    { dispatch: false }
  );

  scope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoggingActions.scope),
      map(({ scope }) => LoggingActions.addGlobalContext({ data: { 'session.scope': scope } }))
    )
  );

  clearScope$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LoggingActions.clearScope),
        tap(() => {
          this.datadogLogsService.removeGlobalContextProperty('session.scope');
        })
      ),
    { dispatch: false }
  );
}
